import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, useDisclosure} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    useAccessToken,
    useCommerceApi,
    useShopperBasketsMutationHelper
} from '@salesforce/commerce-sdk-react'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {useLocation} from 'react-router-dom'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {useGlobaleContext} from '../../../../contexts/globale'
import ProductViewModal from '../../../product-view-modal'
import LoadingSpinner from '../../../loading-spinner'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useToast} from '../../../../hooks/use-toast'
import {useIntl} from 'react-intl'
import {GWP_PROMOTION_COOKIE_ID} from '../../../../constants'
import {getPriceData} from '../../../../utils/product-utils'
import useTagManager from '../../../../hooks/use-tag-manager'
import useMonetate from '../../../../hooks/use-monetate'
import {useWishList} from '../../../../hooks/use-wish-list'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useAddToCartDrawerContext} from '../../../../hooks/use-add-to-cart-drawer'
import {useSiteContext} from '../../../../contexts/site'
import {scheduleIdleAnalytics} from '../../../../utils/common-utils'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {logBasketErrorToSsr} from '../../../../utils/log-basket-error-to-ssr'

/**
 * PdpMonetateCarousel
 *
 * Renders a Monetate-powered product recommendations carousel on the PDP.
 * Handles quick-add-to-cart flow including GWP (Gift With Purchase) promotion
 * logic, Monetate tracking, and Tag Manager events.
 */
export const PdpMonetateCarousel = ({ID, typeId, name}) => {
    // ─── Analytics ───────────────────────────────────────────────────────────
    const tagManager = useTagManager()
    const monetate = useMonetate()

    const analyticsProps = {
        component_id: typeId,
        component_name: name || typeId
    }

    // ─── Customer & Wishlist ─────────────────────────────────────────────────
    const {data: wishlist} = useWishList()
    const {data: customer} = useCurrentCustomer()

    // ─── Site & Globale Context ──────────────────────────────────────────────
    const {setOpenBonusModal} = useSiteContext()
    const {countryISO, currencyCode, globaleCurrency} = useGlobaleContext()

    // ─── Commerce API ────────────────────────────────────────────────────────
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()

    // ─── Routing ─────────────────────────────────────────────────────────────
    const location = useLocation()

    // ─── Messaging ────────────────────────────────────────────────────────────────
    const {formatMessage} = useIntl()

    // ─── Local State ─────────────────────────────────────────────────────────
    const [tileProduct, setTileProduct] = useState()
    const [showSpinner, setShowSpinner] = useState(false)
    const tileProductIndexRef = useRef()

    // ─── Toast ───────────────────────────────────────────────────────────────
    const showToast = useToast()

    const showError = (message, statusCode) => {
        showToast({
            title: statusCode === '403' ? message : formatMessage(message),
            status: 'error'
        })
    }

    // ─── Drawer & Modal ──────────────────────────────────────────────────────
    const {
        isOpen: isAddToCartDrawerOpen,
        onOpen: onAddToCartDrawerOpen,
        onClose: onAddToCartDrawerClose
    } = useAddToCartDrawerContext()

    const {
        isOpen: isQuickAddModalOpen,
        onOpen: onQuickAddModalOpen,
        onClose: onQuickAddModalClose
    } = useDisclosure()

    // ─── Handlers ────────────────────────────────────────────────────────────

    /**
     * Fetches full product data by ID and opens the Quick Add modal.
     * Expands all relevant product associations (images, prices, variations, etc.).
     */
    const fetchProduct = async (productId, indexPosition) => {
        setShowSpinner(true)
        tileProductIndexRef.current = indexPosition
        const token = await getTokenWhenReady()

        const requestParams = {
            id: productId,
            perPricebook: true,
            expand: [
                'availability',
                'promotions',
                'options',
                'images',
                'prices',
                'variations',
                'set_products',
                'page_meta_tags',
                'bundled_products'
            ],
            allImages: true,
            c_selectedCountry: countryISO,
            c_selectedCurrency: currencyCode
        }
        try {
            const product = await api.shopperProducts.getProduct({
                parameters: requestParams,
                headers: {
                    Authorization: `Bearer ${token}`
                },
                keepPreviousData: true
            })
            onQuickAddModalOpen(true)
            // TODO What to happen if we have one SKU only.
            setTileProduct(product)
            setShowSpinner(false)
        } catch (e) {
            setShowSpinner(false)
            console.error('Error occurred in fetching and opening a modal', e.message)
        }
    }

    /**
     * Handles all side-effects after a successful add-to-cart action:
     * - GWP promotion cookie check & bonus modal trigger
     * - Add-to-cart drawer open (when no GWP promo active)
     * - Monetate page tracking
     * - Tag Manager add-to-cart event
     */
    const addToBagActions = async (variant) => {
        try {
            const indexPosition = tileProductIndexRef.current
            const itemsAdded = await addItemToNewOrExistingBasket([
                {
                    productId: variant.productId,
                    price: variant.price,
                    quantity: 1
                }
            ])
            let ga4ItemsAdded = []

            if (itemsAdded) {
                // Prioritise GWP promotions as per business requirement
                const promoId = (
                    itemsAdded?.bonusDiscountLineItems?.find((i) => /gwp/i?.test(i.promotionId)) ||
                    itemsAdded?.bonusDiscountLineItems?.[0]
                )?.promotionId

                const cookie =
                    onClient() &&
                    new CookieStorage({
                        keySuffix: '',
                        sharedContext: !onClient()
                    })

                const gwpPromotionState =
                    onClient() && cookie ? cookie.get(GWP_PROMOTION_COOKIE_ID) : null

                const selectedSku = {
                    ...tileProduct,
                    ...variant
                }
                ga4ItemsAdded = [
                    {
                        product: selectedSku,
                        variant,
                        custom: {
                            price: variant?.price,
                            quantity: 1
                        }
                    }
                ]

                const priceData = getPriceData(selectedSku)

                // Only open the drawer when there is no active GWP promotion awaiting acknowledgement
                if (!(promoId && gwpPromotionState !== 'closed')) {
                    const selectedImage = document
                        .querySelector(
                            "div[data-testid='product-view-modal'] .img-wrapper .cloudinary-image"
                        )
                        ?.getAttribute('src')
                    onAddToCartDrawerOpen({
                        product: selectedSku,
                        itemsAdded,
                        selectedQuantity: 1,
                        priceData,
                        ...(selectedImage && {heroImage: selectedImage})
                    })
                }

                setOpenBonusModal(true)
            }

            scheduleIdleAnalytics(() => {
                try {
                    if (itemsAdded) {
                        monetate.monetateTrackPage(
                            'addToBagModal',
                            window.mtBreadcrumb,
                            itemsAdded?.productItems,
                            customer?.customerType === 'registered',
                            wishlist?.customerProductListItems?.length || 0,
                            null,
                            null,
                            [
                                {
                                    masterId:
                                        (tileProduct?.hitType === 'master'
                                            ? tileProduct?.productId
                                            : '') ||
                                        tileProduct?.master?.masterId ||
                                        tileProduct?.representedProduct?.c_masterVariantID ||
                                        tileProduct?.id,
                                    variantId: variant?.type?.master
                                        ? variant?.variants?.[0]?.productId
                                        : variant?.productId || variant?.id
                                }
                            ],
                            itemsAdded?.giftCertificateItems
                        )
                    }

                    tagManager.addToCart(
                        tileProduct,
                        tileProduct,
                        null,
                        null,
                        'recommender',
                        null,
                        false,
                        {},
                        false,
                        false,
                        false,
                        analyticsProps
                    )
                    tagManager.addToCartGA4({
                        itemsAdded: ga4ItemsAdded,
                        method: 'recommender',
                        currency: globaleCurrency,
                        sorting: undefined,
                        indexPosition,
                        listName: 'recommended for you',
                        listId: 'Recommended For You'
                    })
                } catch (error) {
                    console.error('Error occurred in deferred add-to-bag analytics', error.message)
                }
            })
            setTimeout(() => {
                onQuickAddModalClose()
            }, 3000)
        } catch (error) {
            const errResponse = error?.response ? await error?.response?.json() : null
            logger.error('ATCXL Error adding product to basket', {
                namespace: 'PdpMonetateCarousel.addToBagActions',
                additionalProperties: {error, errResponse, productId: variant?.productId}
            })
            logBasketErrorToSsr('PdpMonetateCarousel.addToBagActions', {
                error,
                errResponse,
                productId: variant?.productId
            })

            if (errResponse?.statusCode === '403') {
                showError(errResponse.statusMessage, errResponse.statusCode)
            } else {
                showError(API_ERROR_MESSAGE)
            }

            setShowSpinner(false)
            throw error
        }
    }

    // ─── Effects ─────────────────────────────────────────────────────────────

    // Close the add-to-cart drawer on route change
    useEffect(() => {
        if (isAddToCartDrawerOpen) {
            onAddToCartDrawerClose()
        }
    }, [location.pathname])

    // ─── Render ──────────────────────────────────────────────────────────────

    if (!ID) return <></>

    return (
        <Box
            className="monetate-recs"
            data-country={countryISO}
            onClick={(e) => {
                const target = e.target?.closest ? e.target : e.target?.parentElement
                const quickAddButton = target?.closest('.mt-quick-add-btn')
                if (!quickAddButton) {
                    return
                }

                const carouselItem = quickAddButton.closest('.mt-merch-item')
                const pid = carouselItem?.getAttribute('data-sku')
                if (pid) {
                    const slideIndex = Number(quickAddButton.closest('[data-slide]')?.dataset.slide)

                    fetchProduct(pid, Number.isInteger(slideIndex) ? slideIndex + 1 : undefined)
                }
            }}
        >
            {showSpinner && <LoadingSpinner position="fixed" top="auto" />}

            <Box id={ID} className={ID}></Box>

            {isQuickAddModalOpen && (
                <ProductViewModal
                    isOpen={isQuickAddModalOpen}
                    onOpen={onQuickAddModalOpen}
                    onClose={onQuickAddModalClose}
                    isQuickAddModal={true}
                    product={tileProduct}
                    showDeliveryOptions={false}
                    updateCart={async (variant) => {
                        setShowSpinner(true)
                        await addToBagActions(variant)
                        setShowSpinner(false)
                    }}
                />
            )}
        </Box>
    )
}

PdpMonetateCarousel.propTypes = {
    ID: PropTypes.string,
    typeId: PropTypes.string,
    name: PropTypes.any
}

export default PdpMonetateCarousel
